// components/ExcelUploader.js
import React, { useState, useRef } from 'react';
import { FileUpload } from 'primereact/fileupload';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';


const ExcelUploader = ({ onFileUpload, disabled }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const toast = useRef(null);

  const validateFile = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidExtension = fileExtension === 'xlsx' || fileExtension === 'xls';
    const isValidType = validTypes.includes(file.type);
    
    if (!isValidExtension && !isValidType) {
      toast.current.show({
        severity: 'error',
        summary: 'Invalid File',
        detail: 'Please upload a valid Excel file (.xlsx or .xls)',
        life: 3000
      });
      return false;
    }
    
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.current.show({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'File size should not exceed 10MB',
        life: 3000
      });
      return false;
    }
    
    return true;
  };

  const handleUpload = async (event) => {
    const file = event.files[0];
    
    if (!validateFile(file)) {
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);
    
    try {
      await onFileUpload(file);
      toast.current.show({
        severity: 'success',
        summary: 'Upload Successful',
        detail: `${file.name} has been uploaded successfully`,
        life: 3000
      });
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.message || 'Failed to upload file',
        life: 5000
      });
    } finally {
      clearInterval(interval);
      setUploadProgress(100);
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 1000);
    }
  };

  const handleSelect = (event) => {
    const file = event.files[0];
    if (file && validateFile(file)) {
      toast.current.show({
        severity: 'info',
        summary: 'File Selected',
        detail: `${file.name} (${(file.size / 1024).toFixed(2)} KB)`,
        life: 3000
      });
    }
  };

  const handleClear = () => {
    setUploading(false);
    setUploadProgress(0);
  };

  return (
    <div className="excel-uploader">
      <Toast ref={toast} />
      
      <div className="card p-4 shadow-2 border-round-xl">
        <h3 className="text-center text-primary mb-3">Import Excel Data</h3>
        
        <FileUpload
          name="excelFile"
          accept=".xlsx,.xls"
          maxFileSize={10485760} // 10MB in bytes
          customUpload={true}
          uploadHandler={handleUpload}
          onSelect={handleSelect}
          onClear={handleClear}
          disabled={disabled || uploading}
          chooseLabel="Choose Excel File"
          cancelLabel="Clear"
          mode="advanced"
          auto={false}
          multiple={false}
          className="p-fluid"
          emptyTemplate={
            <div className="flex flex-column align-items-center justify-content-center p-5">
              <i className="pi pi-file-excel text-6xl text-primary mb-3"></i>
              <p className="text-600 text-center">
                Drag and drop your Excel file here, or click to browse
              </p>
              <small className="text-500">Supported formats: .xlsx, .xls (Max size: 10MB)</small>
            </div>
          }
        />
        
        {uploading && (
          <div className="mt-4">
            <div className="flex align-items-center justify-content-between mb-2">
              <span className="text-sm font-medium">Uploading...</span>
              <span className="text-sm font-medium">{uploadProgress}%</span>
            </div>
            <ProgressBar value={uploadProgress} showValue={false} />
          </div>
        )}
        
        <div className="mt-3 text-center text-500 text-sm">
          <i className="pi pi-info-circle mr-1"></i>
          Make sure your Excel file has the required columns: Loan ID, Customer ID, Customer Name, etc.
        </div>
      </div>
    </div>
  );
};

export default ExcelUploader;
